import { useMemo, useRef } from 'react';

import { FontSize, TEST_IDS } from 'commons';
import {
  Accordion,
  History,
  Loading,
  Modal,
  ReportFail,
  Text,
} from 'components';
import { addToHistoryList } from 'helpers';
import { useAuth } from 'hooks';
import { historyService } from 'services';
import useSWR from 'swr';
import { HistoryContent as HistoryContentType } from 'types';

import { HistoryContent } from '../components';
import * as S from '../styles';

const ORDER_DELIVERED_TYPE = 'ORDER_WAS_DELIVERED';

interface Props {
  consignmentCode: string;
  showDeliveryFail?: boolean;
  defaultOpen?: boolean;
}

export function DashboardHistoryAndTracking({
  consignmentCode,
  showDeliveryFail = false,
  defaultOpen = false,
}: Props) {
  const { selectedStore } = useAuth();
  const store = selectedStore.name || '';
  const toggleModalRef = useRef<HTMLLabelElement>(null);

  const canFetch = Boolean(consignmentCode);

  const { data, isValidating, error } = useSWR(
    canFetch ? `history-${consignmentCode}` : null,
    () =>
      historyService.get({
        consignmentCode: consignmentCode,
        storeNames: [store],
      }),
    { revalidateOnFocus: false }
  );

  const formattedList = useMemo(() => {
    if (data && data.historyEntries) {
      const { historyEntries } = data;

      const historyLength = historyEntries.length;
      const isDelivered = data?.historyEntries.some(
        historyEntry => historyEntry.type === ORDER_DELIVERED_TYPE
      );

      return historyEntries.reduce<HistoryContentType[]>((acc, cur, index) => {
        const isLastEntry = historyLength === index + 1;

        const content = (
          <HistoryContent
            entry={cur}
            isDone={isLastEntry && isDelivered}
            isLast={isLastEntry}
          />
        );

        return addToHistoryList({
          list: acc,
          content,
          date: cur.date,
        });
      }, []);
    }

    return [];
  }, [data]);

  function toggleModal() {
    const { current } = toggleModalRef;

    if (current) {
      current.click();
    }
  }

  function renderContent() {
    if (isValidating) return <Loading />;

    if (error) return <Text>Erro ao carregar histórico. Tente novamente.</Text>;

    if (formattedList?.length) return <History content={formattedList} />;

    return <p>Nenhum registro encontrado</p>;
  }

  return (
    <Accordion
      data-testid={TEST_IDS.TRACKING_HISTORY_ACCORDION}
      title="Histórico e Tracking"
      defaultOpen={defaultOpen}
    >
      <S.Main>
        <S.TextWrapper>
          {showDeliveryFail && (
            <Modal
              modalContent={<ReportFail consignmentCode={consignmentCode} />}
            >
              <label hidden ref={toggleModalRef} />
            </Modal>
          )}

          <Text size={FontSize.font12}>
            Acompanhe o histórico do pedido e seu status de entrega. Caso o
            pedido retorne para a loja, reporte a{' '}
            <S.Report
              $clickable={showDeliveryFail}
              onClick={showDeliveryFail ? toggleModal : undefined}
            >
              “Falha na Entrega”
            </S.Report>{' '}
            através do menu flutuante, assim nosso time de backoffice atuará em
            uma nova tentativa de entrega. Se precisar de ajuda, o menu
            flutuante também permite acionar o time de suporte.
          </Text>
        </S.TextWrapper>

        {renderContent()}
      </S.Main>
    </Accordion>
  );
}
